/**
 * Axios API Layer - Types
 *
 * Backend API response wrapper with standardized error handling
 */

import type { AxiosError, AxiosRequestConfig } from "axios"

/**
 * Backend API Error structure
 */
export interface ApiErrorDto {
  /** Error code (e.g., "NOT_FOUND", "VALIDATION_ERROR") */
  code: string | null
  /** Human-readable error message */
  message: string
  /** Additional error details (can be field errors for validation) */
  details: Record<string, string[]> | Record<string, unknown> | null
}

/**
 * Backend API Response wrapper
 * All API responses follow this structure
 */
export interface BackendResponse<T> {
  /** Whether the request was successful */
  success: boolean
  /** Response data (null on error) */
  data: T | null
  /** Error information (null on success) */
  error: ApiErrorDto | null
  /** Request duration in milliseconds */
  duration: number
}

/**
 * Normalized error structure for UI consumption
 * All API errors are transformed to this format
 */
export interface NormalizedError {
  /** HTTP status code */
  status: number
  /** Error code from backend (e.g., "NOT_FOUND", "VALIDATION_ERROR") */
  code: string | null
  /** Human-readable error message */
  message: string
  /** Field-level validation errors (field name -> error messages) */
  fieldErrors: Record<string, string[]>
  /** Whether this is a network error (no response received) */
  isNetworkError: boolean
  /** Whether this is a timeout error */
  isTimeout: boolean
  /** Whether this is a canceled request */
  isCanceled: boolean
  /** Original error for debugging */
  originalError: AxiosError | null
}

/**
 * Token provider interface for authentication
 * Implement this to provide auth tokens to the API layer
 */
export interface TokenProvider {
  /** Get the current access token */
  getToken(): string | null
  /** Refresh the token (optional, for token refresh flows) */
  refreshToken?(): Promise<string | null>
  /** Clear stored tokens (on logout) */
  clearTokens(): void
}

/**
 * API response wrapper for typed responses (frontend usage)
 */
export interface ApiResponse<T> {
  data: T
  status: number
  statusText: string
}

/**
 * Paginated response from .NET backend
 */
export interface PaginatedResponse<T> {
  items: T[]
  pageNumber: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

/**
 * Extended axios request config with custom options
 */
export interface ApiRequestConfig extends AxiosRequestConfig {
  /** Skip authentication header */
  skipAuth?: boolean
  /** Custom error handler for this request */
  onError?: (error: NormalizedError) => void
  /** Skip global error handling */
  skipGlobalErrorHandler?: boolean
}

/**
 * HTTP method types
 */
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE"

/**
 * Common .NET backend response statuses
 */
export const HttpStatus = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
} as const

export type HttpStatusCode = (typeof HttpStatus)[keyof typeof HttpStatus]
